import { useContext, useEffect } from 'react';
import { FlagsContext } from '../context/FlagsContext/FlagsContext';
import { FLAG_KEYS } from '@/const/flags';
import analytics from '@/util/analytics';
import { datadogRum } from '@datadog/browser-rum';

export const useFlags = () => {
  const contextValue = useContext(FlagsContext);
  return contextValue;
};

export const useAmbassadorRedirectFlag = () => {
  const { value: flagValues } = useFlags();
  return flagValues[FLAG_KEYS.BRAND_AMBASSADOR_REDIRECT_MODAL] === true;
};

// Experiments already exposed in this tab this page load — guards against
// re-firing "Experiment Viewed" on re-renders, or on soft navigations that
// remount a component without a full reload. Scoped per tab/load, not per
// page-view: navigating PLP -> PDP -> back to PLP fires the event once, not
// per view, so per-view rate metrics shouldn't be built on it.
const exposedExperiments = new Set<string>();

/**
 * Reads an A/B test flag's variant and records the exposure in the same
 * call, so a component can't read a variant without the exposure event
 * existing to measure it against.
 *
 * `experimentKey` must be a variant flag (a `flag()` using `bucket()` /
 * `identifyVisitorId` from `@/util/experiment`) — its value is a variant
 * name string, not a boolean.
 *
 * Exposure firing is consent-gated: `analytics` (`@/util/analytics`) drops
 * events for users who've declined analytics consent, so exposure-based
 * analysis measures a consent-selected subpopulation, not everyone bucketed.
 */
export const useExperiment = (experimentKey: FLAG_KEYS): string | undefined => {
  const { value: flagValues } = useFlags();
  const variant = flagValues[experimentKey];
  const variantName = typeof variant === 'string' ? variant : undefined;

  useEffect(() => {
    if (!variantName || exposedExperiments.has(experimentKey)) return;

    console.warn('[segment] Experiment Viewed', {
      experiment_name: experimentKey,
      variant_name: variantName
    });
    analytics.track('Experiment Viewed', {
      experiment_name: experimentKey,
      variant_name: variantName
    });
    datadogRum.addFeatureFlagEvaluation(experimentKey, variantName);
    exposedExperiments.add(experimentKey);
  }, [experimentKey, variantName]);

  return variantName;
};
